import { AgendaItemSubCategory, CallResource, MinutesAnonymization, MinutesVerboseness } from '../../calls.service'
import { Organization } from '../../organizations.service'

export type MinuteTimelineSegment = {
  startSeconds: number
  endSeconds: number
  startPercent: number
  widthPercent: number
}

export enum TemplateContentBlockType {
  FIXED_TEXT = 'FIXED_TEXT',
  VARIABLE_TEXT = 'VARIABLE_TEXT',
  DISCUSSION_SUMMARY = 'DISCUSSION_SUMMARY',
  KEY_MESSAGES = 'KEY_MESSAGES',
  DISCUSSION_CHALLENGE = 'DISCUSSION_CHALLENGE',
  BINARY = 'BINARY',
  MATTERS_ARISING = 'MATTERS_ARISING',
}

export enum TemplateContentBlockSourceType {
  TRANSCRIPT = 'TRANSCRIPT',
  FILE_UPLOAD = 'FILE_UPLOAD',
}

type BaseTemplateContentBlockSource = {
  type: TemplateContentBlockSourceType
  templateContentBlockId: number
}

export type PendingTemplateContentBlockSource = BaseTemplateContentBlockSource & {
  isPending: true
  fileName: string
  callResourceId: null
  callResource: null
}

export type CompletedTemplateContentBlockSource = BaseTemplateContentBlockSource & {
  isPending: false
  callResourceId: number | null
  callResource: CallResource | null
}

export type TemplateContentBlockSource = PendingTemplateContentBlockSource | CompletedTemplateContentBlockSource

export type FormattedTextBlock = {
  content: string
  index: number
}

export type FixedTextData = {
  formattedTextBlock: FormattedTextBlock
}

export type VariableTextData = {
  formattedTextBlock: FormattedTextBlock
}

export type DiscussionSummaryData = {
  guidance: string
}

export type KeyMessagesData = {
  guidance: string
}

export type DiscussionChallengeData = {
  guidance: string
}

export type MattersArisingData = {
  guidance: string
}

export type BinaryData = {
  formattedTextBlocks: FormattedTextBlock[]
}

type BaseTemplateContentBlock = {
  id: number
  index: number
  sources: TemplateContentBlockSource[]
  templateItemId: number
  levelOfDetail: MinutesVerboseness
  anonymization: MinutesAnonymization
  category: AgendaItemSubCategory | null
}

export type FixedTextContentBlock = BaseTemplateContentBlock & {
  type: TemplateContentBlockType.FIXED_TEXT
  data: FixedTextData
}

export type VariableTextContentBlock = BaseTemplateContentBlock & {
  type: TemplateContentBlockType.VARIABLE_TEXT
  data: VariableTextData
}

export type DiscussionSummaryContentBlock = BaseTemplateContentBlock & {
  type: TemplateContentBlockType.DISCUSSION_SUMMARY
  data: DiscussionSummaryData
}

export type KeyMessagesContentBlock = BaseTemplateContentBlock & {
  type: TemplateContentBlockType.KEY_MESSAGES
  data: KeyMessagesData
}

export type DiscussionChallengeContentBlock = BaseTemplateContentBlock & {
  type: TemplateContentBlockType.DISCUSSION_CHALLENGE
  data: DiscussionChallengeData
}

export type MattersArisingContentBlock = BaseTemplateContentBlock & {
  type: TemplateContentBlockType.MATTERS_ARISING
  data: MattersArisingData
}

export type BinaryContentBlock = BaseTemplateContentBlock & {
  type: TemplateContentBlockType.BINARY
  data: BinaryData
}

export type TemplateContentBlock =
  | FixedTextContentBlock
  | VariableTextContentBlock
  | DiscussionSummaryContentBlock
  | KeyMessagesContentBlock
  | DiscussionChallengeContentBlock
  | MattersArisingContentBlock
  | BinaryContentBlock

export type TemplateItem = {
  id: number
  index: number
  title: string
  presenters: string[]
  duration: number | null
  isSectionHeading: boolean
  contentBlocks: TemplateContentBlock[]
  parentId: number | null
  parent?: TemplateItem
  subitems: TemplateItem[]
  minuteTimelineSegments: MinuteTimelineSegment[]
  hasMinuteContent: boolean
}

export type TemplateItemWithExpandedState = TemplateItem & {
  expanded: boolean
  subitems: TemplateItemWithExpandedState[]
}

export type TemplateType = 'MODEL' | 'INSTANCE'

export type Template = {
  id: number
  name: string
  type: TemplateType
  items: TemplateItem[]
  organizationId: number
  organization?: Organization
}

export type FlattenedTemplateItem = TemplateItemWithExpandedState & {
  indentLevel: number
  itemNumber: string
}
